import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { detectBrowserLocale } from './detect';
import en from './locales/en';
import { baseLocaleCode, normalizeLocaleCode, pickBestLocale } from './supportedLocales';

type I18nContextValue = {
  locale: string;
  setLocale: (nextLocale: string) => void;
  timezone: string;
  setTimezone: (nextTimezone: string) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
  dbLoaded: boolean;
  generationStatus: {
    totalKeys: number;
    translatedKeys: number;
    missingKeys: number;
    aiGeneratedKeys: number;
    manualKeys: number;
    needsReviewKeys: number;
  } | null;
  aiPreparing: boolean;
  aiError: string | null;
  requestAiFill: (nextLocale?: string) => Promise<{ ok: boolean; error?: string }>;
};

const dictionaries = {
  en,
} as const;

const I18nContext = React.createContext<I18nContextValue | undefined>(undefined);

type I18nProviderProps = {
  children: React.ReactNode;
  initialLocale?: string | null;
  initialTimezone?: string | null;
};

const resolveTimezone = (initialTimezone?: string | null) => {
  const trimmed = initialTimezone?.trim();
  if (trimmed) return trimmed;
  if (typeof window !== 'undefined') {
    try {
      const stored = window.localStorage.getItem('ua_timezone');
      if (stored && stored.trim()) return stored.trim();
    } catch {
      // ignore storage errors
    }
  }
  try {
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (browserTimezone && browserTimezone.trim()) {
      return browserTimezone.trim();
    }
  } catch {
    // ignore browser timezone errors
  }
  return 'UTC';
};

export function I18nProvider({
  children,
  initialLocale,
  initialTimezone,
}: I18nProviderProps) {
  const [locale, setLocaleState] = React.useState<string>(() =>
    normalizeLocaleCode(initialLocale ?? 'en'),
  );
  const [availableLocales, setAvailableLocales] = React.useState<string[]>(['en']);
  const availableLocalesRef = React.useRef<string[]>(availableLocales);
  const [dbTranslations, setDbTranslations] = React.useState<Record<string, string>>({});
  const [dbLoaded, setDbLoaded] = React.useState(false);
  const [aiPreparing, setAiPreparing] = React.useState(false);
  const [aiError, setAiError] = React.useState<string | null>(null);
  const [aiRequestedAt, setAiRequestedAt] = React.useState<number | null>(null);
  const [timezone, setTimezone] = React.useState(() => resolveTimezone(initialTimezone));
  const queryClient = useQueryClient();
  const installAttemptedRef = React.useRef(false);
  const initialTimezoneRef = React.useRef(initialTimezone);

  React.useEffect(() => {
    availableLocalesRef.current = availableLocales;
  }, [availableLocales]);

  const setLocale = React.useCallback(
    (nextLocale: string) => {
      const normalized = normalizeLocaleCode(nextLocale);
      const base = baseLocaleCode(normalized);
      const hasAvailable = [normalized, base].some(
        (candidate) => candidate && availableLocales.includes(candidate),
      );
      const resolved = hasAvailable
        ? pickBestLocale(nextLocale, availableLocales, 'en')
        : normalized || 'en';
      setLocaleState(resolved);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('ua_locale', resolved);
      }
    },
    [availableLocales],
  );

  const setTimezoneValue = React.useCallback((nextTimezone: string) => {
    const trimmed = nextTimezone.trim();
    const resolved = trimmed || 'UTC';
    setTimezone(resolved);
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem('ua_timezone', resolved);
      } catch {
        // ignore storage errors
      }
    }
  }, []);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    let mounted = true;

    const loadLocales = async () => {
      try {
        const res = await fetch('/api/i18n/locales');
        if (!res.ok) {
          throw new Error('Failed to load locales');
        }
        const data = (await res.json()) as {
          ok?: boolean;
          locales?: Array<{ code: string }>;
        };
        const codes = Array.isArray(data?.locales)
          ? data.locales
              .map((entry) => normalizeLocaleCode(entry.code))
              .filter(Boolean)
          : [];
        const uniqueCodes = Array.from(new Set(codes));
        const resolvedAvailable = uniqueCodes.length ? uniqueCodes : ['en'];
        if (!mounted) return;
        setAvailableLocales(resolvedAvailable);
        const storedLocale = window.localStorage.getItem('ua_locale');
        const preferredLocale = initialLocale || storedLocale || detectBrowserLocale();
        const normalizedPreferred = normalizeLocaleCode(preferredLocale);
        const basePreferred = baseLocaleCode(normalizedPreferred);
        const hasPreferred = [normalizedPreferred, basePreferred].some(
          (candidate) => candidate && resolvedAvailable.includes(candidate),
        );

        if (!hasPreferred && !installAttemptedRef.current) {
          installAttemptedRef.current = true;
          try {
            const installRes = await fetch('/api/i18n/install-locale', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ locale: preferredLocale }),
            });
            const installData = (await installRes.json().catch(() => null)) as {
              ok?: boolean;
              locale?: string;
            } | null;
            if (installRes.ok && installData?.ok && installData.locale) {
              const installedCode = normalizeLocaleCode(installData.locale);
              const refreshedRes = await fetch('/api/i18n/locales');
              if (!refreshedRes.ok) {
                throw new Error('Failed to reload locales');
              }
              const refreshedData = (await refreshedRes.json()) as {
                ok?: boolean;
                locales?: Array<{ code: string }>;
              };
              const refreshedCodes = Array.isArray(refreshedData?.locales)
                ? refreshedData.locales
                    .map((entry) => normalizeLocaleCode(entry.code))
                    .filter(Boolean)
                : [];
              const refreshedAvailable = Array.from(
                new Set(refreshedCodes.length ? refreshedCodes : ['en']),
              );
              if (!mounted) return;
              setAvailableLocales(refreshedAvailable);
              setLocaleState(installedCode || 'en');
              window.localStorage.setItem('ua_locale', installedCode || 'en');
              return;
            }
          } catch {
            // fallback below
          }
        }

        const resolvedLocale = pickBestLocale(preferredLocale, resolvedAvailable, 'en');
        setLocaleState(resolvedLocale);
        window.localStorage.setItem('ua_locale', resolvedLocale);
      } catch {
        if (!mounted) return;
        const storedLocale = window.localStorage.getItem('ua_locale');
        const preferredLocale = initialLocale || storedLocale || detectBrowserLocale();
        const resolvedLocale = pickBestLocale(
          preferredLocale,
          availableLocalesRef.current,
          'en',
        );
        setLocaleState(resolvedLocale);
        window.localStorage.setItem('ua_locale', resolvedLocale);
      }
    };

    const loadTimezone = () => {
      const resolved = resolveTimezone(initialTimezone);
      if (mounted) {
        setTimezone(resolved);
      }
      if (resolved) {
        try {
          window.localStorage.setItem('ua_timezone', resolved);
        } catch {
          // ignore storage errors
        }
      }
    };

    loadLocales();
    loadTimezone();

    return () => {
      mounted = false;
    };
  }, [initialLocale, initialTimezone]);

  React.useEffect(() => {
    if (initialTimezoneRef.current === initialTimezone) return;
    initialTimezoneRef.current = initialTimezone;
    const resolved = resolveTimezone(initialTimezone);
    setTimezone(resolved);
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem('ua_timezone', resolved);
      } catch {
        // ignore storage errors
      }
    }
  }, [initialTimezone]);

  React.useEffect(() => {
    setAiPreparing(false);
    setAiError(null);
    setAiRequestedAt(null);
  }, [locale]);

  const translationsQuery = useQuery({
    queryKey: ['i18n', locale],
    queryFn: async () => {
      const res = await fetch(
        `/api/i18n/translations?locale=${encodeURIComponent(locale)}&namespace=app`,
      );
      if (!res.ok) {
        throw new Error('Failed to load translations');
      }
      const data = (await res.json()) as {
        ok?: boolean;
        translations?: Record<string, string>;
      };
      return data?.translations ?? {};
    },
    enabled: typeof window !== 'undefined',
    staleTime: 5 * 60 * 1000,
  });

  const statusQuery = useQuery({
    queryKey: ['i18n-status', locale],
    queryFn: async () => {
      const res = await fetch(
        `/api/i18n/generation-status?locale=${encodeURIComponent(locale)}&namespace=app`,
      );
      if (!res.ok) {
        throw new Error('Failed to load translation status');
      }
      const data = (await res.json()) as {
        ok?: boolean;
        totalKeys?: number;
        translatedKeys?: number;
        missingKeys?: number;
        aiGeneratedKeys?: number;
        manualKeys?: number;
        needsReviewKeys?: number;
      };
      return {
        totalKeys: data?.totalKeys ?? 0,
        translatedKeys: data?.translatedKeys ?? 0,
        missingKeys: data?.missingKeys ?? 0,
        aiGeneratedKeys: data?.aiGeneratedKeys ?? 0,
        manualKeys: data?.manualKeys ?? 0,
        needsReviewKeys: data?.needsReviewKeys ?? 0,
      };
    },
    enabled: typeof window !== 'undefined' && locale !== 'en',
    staleTime: 30 * 1000,
    refetchInterval: aiPreparing ? 2000 : false,
  });

  React.useEffect(() => {
    if (translationsQuery.data) {
      setDbTranslations(translationsQuery.data);
      setDbLoaded(true);
    }
  }, [translationsQuery.data]);

  React.useEffect(() => {
    if (!aiPreparing || !aiRequestedAt) return;
    const elapsed = Date.now() - aiRequestedAt;
    if (elapsed > 60 * 1000) {
      setAiPreparing(false);
      setAiError('Translation generation timed out.');
      return;
    }
    if (statusQuery.data && statusQuery.data.missingKeys === 0) {
      setAiPreparing(false);
      setAiError(null);
      queryClient.invalidateQueries({ queryKey: ['i18n', locale] });
    }
  }, [aiPreparing, aiRequestedAt, locale, queryClient, statusQuery.data]);

  const requestAiFill = React.useCallback(
    async (nextLocale?: string) => {
      const targetLocale = pickBestLocale(nextLocale ?? locale, availableLocales, 'en');
      if (targetLocale === 'en') {
        return { ok: false, error: 'English is already available.' };
      }
      setAiError(null);
      setAiPreparing(true);
      setAiRequestedAt(Date.now());
      try {
        const res = await fetch('/api/i18n/generate-missing', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            locale: targetLocale,
            namespace: 'app',
            limit: 50,
            overwrite: false,
            needsReview: true,
          }),
        });
        const data = (await res.json().catch(() => null)) as { ok?: boolean; message?: string };
        if (!res.ok || !data?.ok) {
          const message = data?.message || 'Unable to prepare translations.';
          setAiPreparing(false);
          setAiError(message);
          return { ok: false, error: message };
        }
        queryClient.invalidateQueries({ queryKey: ['i18n-status', targetLocale] });
        return { ok: true };
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unable to prepare translations.';
        setAiPreparing(false);
        setAiError(message);
        return { ok: false, error: message };
      }
    },
    [availableLocales, locale, queryClient],
  );

  const t = React.useCallback(
    (key: string, params?: Record<string, string | number>) => {
      const template =
        dbTranslations[key] ??
        dictionaries[locale]?.[key] ??
        dictionaries.en[key] ??
        key;
      if (!params) return template;
      return template.replace(/\{(\w+)\}/g, (_, token: string) => {
        const value = params[token];
        return value == null ? `{${token}}` : String(value);
      });
    },
    [dbTranslations, locale],
  );

  const value = React.useMemo(
    () => ({
      locale,
      setLocale,
      timezone,
      setTimezone: setTimezoneValue,
      t,
      dbLoaded,
      generationStatus: statusQuery.data ?? null,
      aiPreparing,
      aiError,
      requestAiFill,
    }),
    [
      aiError,
      aiPreparing,
      dbLoaded,
      locale,
      requestAiFill,
      setLocale,
      setTimezoneValue,
      statusQuery.data,
      t,
      timezone,
    ],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = React.useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
}
